import type { Enabler, LearningCatalog, Objective, Resource } from "../../types";
import type { ChatbotConfidence, ChatbotIntentId, QueryProfiles, ResourceRecommendation } from "../types";
import type { QueryMatchContext } from "./query";
import { normalizeText } from "./query";

type Indexes = {
  objectivesById: Map<string, Objective>;
  objectivesByCode: Map<string, Objective>;
  enablersByCode: Map<string, Enabler>;
};

export function recommendResources(
  catalog: LearningCatalog,
  matchContext: QueryMatchContext,
  intent: ChatbotIntentId,
  maxResults: number,
  profiles: QueryProfiles
): { recommendations: ResourceRecommendation[]; confidence: ChatbotConfidence } {
  const indexes: Indexes = {
    objectivesById: new Map(catalog.objectives.map((objective) => [objective.id, objective])),
    objectivesByCode: new Map(catalog.objectives.map((objective) => [objective.code, objective])),
    enablersByCode: new Map(catalog.enablers.map((enabler) => [enabler.code, enabler]))
  };

  const recommendations = catalog.resources
    .map((resource) => scoreResource(resource, matchContext, intent, indexes, profiles))
    .filter((recommendation) => recommendation.score > 0)
    .sort((a, b) => b.score - a.score || a.resource.name.localeCompare(b.resource.name))
    .slice(0, maxResults);

  return {
    recommendations,
    confidence: confidenceForScore(recommendations[0]?.score ?? 0)
  };
}

function scoreResource(
  resource: Resource,
  matchContext: QueryMatchContext,
  intent: ChatbotIntentId,
  indexes: Indexes,
  profiles: QueryProfiles
): ResourceRecommendation {
  const query = matchContext.query;
  const objectiveText = resource.objectiveIds
    .map((id) => indexes.objectivesById.get(id))
    .filter(Boolean)
    .map((objective) => `${objective?.code} ${objective?.stage} ${objective?.title} ${objective?.description ?? ""}`)
    .join(" ");
  const enablerText = resource.enablerIds
    .map((code) => indexes.enablersByCode.get(code))
    .filter(Boolean)
    .map((enabler) => `${enabler?.code} ${enabler?.theme} ${enabler?.title} ${enabler?.definition}`)
    .join(" ");
  const keywordText = normalizeText((resource.keywords ?? []).join(" "), profiles);
  const titleText = normalizeText(resource.name, profiles);
  const summaryText = normalizeText(resource.summary ?? "", profiles);
  const formatText = normalizeText(resource.format, profiles);
  const normalizedObjectiveText = normalizeText(objectiveText, profiles);
  const normalizedEnablerText = normalizeText(enablerText, profiles);
  const primaryText = normalizeText(
    [
      resource.name,
      resource.summary ?? "",
      resource.format,
      resource.category ?? "",
      ...(resource.topics ?? []),
      keywordText,
      ...(resource.stages ?? []),
      resource.difficulty ?? ""
    ].join(" "),
    profiles
  );
  const contextText = `${normalizedObjectiveText} ${normalizedEnablerText}`;
  const allResourceText = `${primaryText} ${contextText}`;
  const requestedFormats = getRequestedFormats(matchContext, profiles);
  const requestedFormatMatches = requestedFormats.length === 0 || requestedFormats.some((format) => formatText.includes(format));
  if (!requestedFormatMatches) {
    return {
      resource,
      score: 0,
      why: "",
      matchReasons: []
    };
  }

  const resourceObjectiveCodes = resource.objectiveIds
    .map((id) => indexes.objectivesById.get(id)?.code)
    .filter((code): code is string => Boolean(code));
  const matchedObjectiveCodes = resourceObjectiveCodes.filter((code) => matchContext.objectiveScores.has(code));
  const topicScore = (resource.topics ?? []).reduce((total, topic) => total + (matchContext.topicScores.get(topic) ?? 0), 0);
  const objectiveScore = matchedObjectiveCodes.reduce((total, code) => total + (matchContext.objectiveScores.get(code) ?? 0), 0);
  const facetBoost = getFacetBoost(resource, matchContext, profiles, allResourceText);
  const primaryTermMatches = query.expandedTerms.filter((term) => primaryText.includes(term)).length;
  const contextTermMatches = query.expandedTerms.filter((term) => contextText.includes(term)).length;
  const objectiveTermMatches = query.expandedTerms.filter((term) => normalizedObjectiveText.includes(term)).length;
  const enablerTermMatches = query.expandedTerms.filter((term) => normalizedEnablerText.includes(term)).length;
  const keywordMatches = query.terms.filter((term) => keywordText.includes(term)).length;
  const titleMatches = query.terms.filter((term) => titleText.includes(term)).length;
  const summaryMatches = query.terms.filter((term) => summaryText.includes(term)).length;
  const formatMatches = query.terms.filter((term) => formatText.includes(term)).length;
  const stageMatches = (resource.stages ?? []).filter((stage) => query.normalized.includes(normalizeText(stage, profiles))).length;
  const exactPhrase =
    query.normalized.length > 4 && (titleText.includes(query.normalized) || summaryText.includes(query.normalized));
  const hasDirectEvidence = Boolean(
    titleMatches ||
      exactPhrase ||
      topicScore ||
      objectiveScore ||
      facetBoost ||
      summaryMatches ||
      keywordMatches ||
      formatMatches ||
      stageMatches
  );
  const recommendationBoost = hasDirectEvidence ? recommendationBoostFor(resource, matchedObjectiveCodes) : 0;
  const wantsShort = intent === "short_resource" || /\b(short|quick|brief|fast|ten minutes|15 minutes)\b/.test(query.normalized);
  const shortBoost = wantsShort && (resource.estimatedMinutes ?? 999) <= 20 ? 6 : 0;

  const score = hasDirectEvidence
    ? titleMatches * 12 +
      (exactPhrase ? 10 : 0) +
      topicScore * 2 +
      objectiveScore * 3 +
      facetBoost +
      summaryMatches * 5 +
      keywordMatches * 4 +
      primaryTermMatches * 2 +
      objectiveTermMatches * 3 +
      Math.min(contextTermMatches, 5) +
      Math.min(enablerTermMatches, 3) +
      stageMatches * 5 +
      formatMatches * 4 +
      recommendationBoost +
      shortBoost
    : 0;

  const signals: ResourceMatchSignals = {
    topicScore,
    objectiveScore,
    facetBoost,
    exactPhrase,
    titleMatches,
    summaryMatches,
    keywordMatches,
    formatMatches,
    objectiveMatches: matchedObjectiveCodes.length,
    stageMatches,
    recommendationBoost
  };

  return {
    resource,
    score,
    why: buildWhy(resource, signals),
    matchReasons: buildMatchReasons(resource, signals)
  };
}

function getFacetBoost(resource: Resource, matchContext: QueryMatchContext, profiles: QueryProfiles, allResourceText: string): number {
  let boost = 0;
  for (const facetId of matchContext.matchedFacets) {
    const facet = profiles.facets[facetId];
    if (!facet) {
      continue;
    }
    const resourceTerms = facet.resourceTerms ?? facet.terms;
    const resourceMatches = resourceTerms.filter((term) => allResourceText.includes(normalizeText(term, profiles))).length;
    if (resourceMatches === 0) {
      continue;
    }
    const facetScore = matchContext.facetScores.get(facetId) ?? 1;
    boost += Math.min(resourceMatches, 2) * facet.boost + Math.min(facetScore, 3);
    for (const topicId of resource.topics ?? []) {
      boost += profiles.topics[topicId]?.facetBoosts?.[facetId] ?? 0;
    }
  }
  return boost;
}

function getRequestedFormats(matchContext: QueryMatchContext, profiles: QueryProfiles): string[] {
  const formats = [
    ["video", "video"],
    ["webinar", "video"],
    ["article", "article"],
    ["podcast", "podcast"],
    ["book", "book"],
    ["guide", "guide"],
    ["pdf", "pdf"],
    ["research", "research"],
    ["tutorial", "tutorial"],
    ["interview", "interview"]
  ] as const;

  return formats.filter(([term]) => matchContext.query.terms.includes(normalizeText(term, profiles))).map(([, format]) => format);
}

function recommendationBoostFor(resource: Resource, matchedObjectiveCodes: string[]): number {
  if (matchedObjectiveCodes.length > 0) {
    const topObjectiveCount = Math.max(
      0,
      ...matchedObjectiveCodes.map((code) => resource.recommendationCountsByObjective?.[code] ?? 0)
    );
    return Math.min(topObjectiveCount, 8);
  }

  return Math.min(resource.recommendationCount ?? 0, 8);
}

function buildWhy(
  resource: Resource,
  signals: ResourceMatchSignals
): string {
  const reasons = buildMatchReasons(resource, signals);
  if (reasons.length > 0) {
    return reasons.slice(0, 2).join(" ");
  }

  return "It is connected through matching key objectives and enablers.";
}

type ResourceMatchSignals = {
  topicScore: number;
  objectiveScore: number;
  facetBoost: number;
  exactPhrase: boolean;
  titleMatches: number;
  summaryMatches: number;
  keywordMatches: number;
  formatMatches: number;
  objectiveMatches: number;
  stageMatches: number;
  recommendationBoost: number;
};

function buildMatchReasons(resource: Resource, signals: ResourceMatchSignals): string[] {
  const reasons = [];
  if (signals.titleMatches > 0) {
    reasons.push("The title closely matches your wording.");
  }
  if (signals.exactPhrase) {
    reasons.push("It contains the exact phrase from your question.");
  }
  if (signals.topicScore > 0) {
    reasons.push("It matches the startup topic detected by the guide.");
  }
  if (signals.objectiveMatches > 0 || signals.objectiveScore > 0) {
    reasons.push("It maps to a matching Navigator key objective.");
  }
  if (signals.facetBoost > 0) {
    reasons.push("It matches the buyer or startup context in your request.");
  }
  if (signals.stageMatches > 0 && resource.stages?.length) {
    reasons.push(`It fits the ${resource.stages.slice(0, 2).join(" / ")} stage.`);
  }
  if (signals.formatMatches > 0) {
    reasons.push(`It is a ${resource.format}, matching the requested format.`);
  }
  if (signals.summaryMatches > 0) {
    reasons.push("Its summary contains relevant terms from your question.");
  }
  if (signals.keywordMatches > 0) {
    reasons.push("Its metadata keywords match the request.");
  }
  if (signals.recommendationBoost > 0) {
    reasons.push("It has strong recommendation signals in the catalogue.");
  }
  return reasons;
}

function confidenceForScore(score: number): ChatbotConfidence {
  if (score >= 30) {
    return "high";
  }
  if (score >= 8) {
    return "medium";
  }
  return "low";
}
